import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { OAuthEvent, OAuthService, OAuthErrorEvent } from 'angular-oauth2-oidc';
import { IOAuthErrorEventParams } from './IOAuthErrorEventParams';
import { authConfigPKCE } from './auth.config';
import { UserProfileData } from '../../shared/models/UserProfileData';
import { IUserGroup } from '../../shared/models/UserGroups/IUserGroup';
import { UserGroup } from '../../shared/models/UserGroups/UserGroup';
import { EUserGroupTypes } from '../../shared/models/UserGroups/eUserGroupTypes';
import { EUserGroupRoles } from '../../shared/models/UserGroups/eUserGroupRoles';
import { IB2CUser } from '../../shared/models/UserGroups/IB2CUser';
import { B2CUser } from '../../shared/models/UserGroups/B2CUser';
import { environment } from '../../../environments/environment';

/**
 * The AuthService class provides methods for handling authentication and user profile management
 * using OAuth2 and OpenID Connect protocols. It integrates with the OAuthService to manage login,
 * logout, and user session handling, including support for password reset and profile editing flows.
 * 
 * @class
 * @implements {OnDestroy}
 * 
 * @property {Subscription} subscriptions - Manages all subscriptions to avoid memory leaks.
 * @property {Subject<boolean>} isLoggedInSubject - Emits the login status of the user.
 * @property {BehaviorSubject<boolean>} isDoneLoadingSubject - Emits the loading status of the authentication service.
 * @property {IB2CUser | undefined} currentUser - Stores the current authenticated user.
 * @property {Observable<boolean>} isDoneLoading$ - An observable that notifies when the authentication service loading has finished.
 * 
 * @constructor
 * @param {OAuthService} oAuthService - The OAuth service to configure and use for authentication.
 * @param {Router} router - The Angular router for performing redirects.
 * 
 * @method getUserProfileData - Gets the user claims as a user profile object.
 * @returns {UserProfileData | null} The user profile data.
 * 
 * @method bootstrapOAuthService - Configures and sets up the OAuth service.
 * @returns {Promise<void>} A promise that resolves when the OAuth service is configured.
 * 
 * @method login - Starts the OAuth login flow.
 * 
 * @method loginWithRedirect - Starts the OAuth login flow and passes a redirect URI to access after the login.
 * @param {string} redirectUri - The URI to access after the login.
 * 
 * @method editProfile - Starts the edit user profile flow.
 * 
 * @method logout - Logs the current user out.
 * 
 * @method hasValidAccessToken - Determines if the current user has a valid access token.
 * @returns {boolean} True if the user has a valid access token; else false.
 * 
 * @method claims - Gets the currently authenticated user's claims.
 * @returns {Record<string, unknown>} The user's claims.
 * 
 * @method getUsersCustomerIds - Gets the IDs of the customers the currently authenticated user is assigned to.
 * @returns {IUserGroup[] | undefined} The IDs of the user's customers.
 * 
 * @method isUserCustomerAdmin - Gets a value that determines if the user is an admin.
 * @returns {boolean} True if the user is an admin; else false.
 * 
 * @method getCurrentUserId - Gets the user ID of the currently authenticated user.
 * @returns {string | undefined} The ID of the user.
 * 
 * @method ngOnDestroy - Unsubscribes from all open subscriptions.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  private subscriptions: Subscription = new Subscription();
  private isLoggedInSubject: Subject<boolean> = new Subject<boolean>();
  private isDoneLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private currentUser: IB2CUser | undefined;

  /** An observable that notifies when the authentication service loading has finished. */
  public isDoneLoading$ = this.isDoneLoadingSubject.asObservable();

  /**
   * Initializes a new instance of the {@link AuthService} class.
   * @param oAuthService The OAuth service to configure and use for authentication.
   * @param router The angular router for performing redirects.
   */
  constructor(
    private oAuthService: OAuthService,
    private router: Router) {
  }

  /**
   * Retrieves the user profile data from the OAuth service.
   *
   * @returns {UserProfileData | null} The user profile data containing the given name, family name, and email,
   * or null if the identity claims are not available.
   */
  public getUserProfileData(): UserProfileData | null {
    let claims = this.oAuthService.getIdentityClaims();
    if (!claims) {
      return null;
    }
    return new UserProfileData(claims.given_name, claims.family_name, claims.email);
  }

  /**
   * Initializes and configures the OAuth service for the application.
   * 
   * This method performs the following steps:
   * 1. Configures the OAuth service with PKCE.
   * 2. Sets up automatic silent refresh for the OAuth tokens.
   * 3. Subscribes to OAuth service events to handle token reception and redirection.
   * 4. Loads the discovery document from the specified URL.
   * 5. Handles different user flows such as password reset and profile editing.
   * 6. Attempts to log in using the code flow.
   * 7. Handles various OAuth errors and redirects appropriately.
   * 
   * @returns {Promise<void>} A promise that resolves when the OAuth service is fully initialized.
   */
  public async bootstrapOAuthService(): Promise<void> {
    this.oAuthService.configure(authConfigPKCE);
    this.oAuthService.setupAutomaticSilentRefresh();
    const oAuthServiceEvents$ = this.oAuthService.events.subscribe(
      (event: OAuthEvent) => {
        if (event.type === 'token_received') {
          this.isLoggedInSubject.next(true);
          // Redirect durchführen falls ein state vorhanden ist
          // Siehe: https://github.com/manfredsteyer/angular-oauth2-oidc/issues/592
          if (this.oAuthService.state) {
            const redirect_uri = decodeURIComponent(this.oAuthService.state);
            if (redirect_uri && redirect_uri !== '/') {
              this.router.navigateByUrl(redirect_uri);
            }
          }
        }
      }
    );
    this.subscriptions.add(oAuthServiceEvents$);
    const url =
      authConfigPKCE.issuer +
      '.well-known/openid-configuration?p=' + environment.b2cSigninOnly;

    // The convenience method mentioned in the docs (loadDiscoveryDocumentAndLogin) won't work
    // since we need a way to modify the token endpoint
    return this.oAuthService
      .loadDiscoveryDocument(url)
      .then(() => {
        if (this.userHasEnteredPasswordResetFlow()) {
          // We need to change to token endpoint to match the reset-password flow
          this.oAuthService.tokenEndpoint?.replace(
            environment.b2cSigninOnly.toLowerCase(),
            environment.b2cPasswordReset
          );
        }

        if (this.userHasEnteredEditProfileFlow()) {
          this.oAuthService.tokenEndpoint = 'https://adbihlerb2c.b2clogin.com/68bbf77d-1ca3-4a8f-85c0-c34013ace1f1/oauth2/v2.0/token?p=' + environment.b2cEditProfile;
          return this.oAuthService.initImplicitFlow();
        }

        return this.oAuthService.tryLoginCodeFlow().then(() => {
          this.isDoneLoadingSubject.next(true);
          this.initCurrentUser();
          return Promise.resolve();
        });
      })
      .catch((oAuthErrorEvent: OAuthErrorEvent) => {
        if (oAuthErrorEvent instanceof OAuthErrorEvent) {
          const parm: IOAuthErrorEventParams = oAuthErrorEvent.params as IOAuthErrorEventParams;
          if (
            parm.error === 'access_denied' &&
            parm.error_description.startsWith('AADB2C90118')
          ) {
            // In this case we need to enter a different flow on the Azure AD B2C side.
            // This is still a valid Code + PKCE flow, but uses a different form to support self service password reset
            this.oAuthService.loginUrl = this.oAuthService.loginUrl?.replace(
              environment.b2cSigninOnly.toLowerCase(),
              environment.b2cPasswordReset
            );
            // Add this to the state as we need it on our way back
            this.oAuthService.initCodeFlow('PASSWORD_RESET');
          } else if (
            parm.error === 'access_denied' &&
            parm.error_description.startsWith('AADB2C90091')
          ) {
            // user has cancelled out of password reset
            this.router.navigate(['/']);
          } else if (parm.error === 'redirect_uri_mismatch') {
            this.router.navigate(['/']);
            this.oAuthService.initCodeFlow();
          } else {
            // Another error has occurred, e.g. the user cancelled the reset-password flow.
            // In that case, simply retry the login.
            this.oAuthService.initCodeFlow();
          }
        }
        this.isDoneLoadingSubject.next(true);
        return Promise.resolve();
      });
  }

  private initCurrentUser(): void {
    let claims = this.oAuthService.getIdentityClaims();
    if (!claims) {
      return;
    }
    var groups: IUserGroup[] = [];
    var user = new B2CUser(claims.given_name, claims.family_name, claims.email, claims.sub, '', groups);
    const userGroups = this.claims.groups as string[];
    userGroups.forEach(group => {
      var userGroup = this.convertStringToUserGroup(group);
      if (userGroup != undefined) {
        user.groups.push(userGroup);
      }
    });
    this.currentUser = user;
  }

  private userHasEnteredPasswordResetFlow(): boolean {
    return window.location.search.indexOf('PASSWORD_RESET') > -1;
  }

  private userHasEnteredEditProfileFlow(): boolean {
    return window.location.search.indexOf('EDIT_PROFILE') > -1;
  }

  /**
   * Starts the OAuth login flow.
   */
  public login(): void {
    if (!this.oAuthService.hasValidAccessToken()) {
      this.oAuthService.initCodeFlow();
    }
  }

  /**
   * Starts the OAuth login flow and passes a redirect URI to access after the login.
   * @param redirectUri The URI to access after the login.
   */
  public loginWithRedirect(redirectUri: string): void {
    if (!this.oAuthService.hasValidAccessToken()) {
      this.oAuthService.initCodeFlow(redirectUri);
    }
  }

  /**
   * Starts the edit user profile flow.
   */
  public editProfile(): void {
    if (this.oAuthService.hasValidAccessToken()) {
      this.oAuthService.loginUrl = this.oAuthService.loginUrl?.replace(
        environment.b2cSigninOnly.toLowerCase(),
        environment.b2cEditProfile
      );
      this.oAuthService.initImplicitFlow('EDIT_PROFILE');
    }
  }

  /**
   * Logs the current user out.
   */
  public logout(): void {
    this.oAuthService.logOut();
  }

  /**
   * Determines if the current user has a valid access token.
   * @returns True if the user has a valid access token; else false.
   */
  public hasValidAccessToken(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }

  /**
   * Gets the currently authenticated users claims.
   */
  public get claims(): Record<string, unknown> {
    return this.oAuthService.getIdentityClaims() as Record<string, unknown>;
  }

  /**
   * Gets the IDs of the customers the currently authenticated user is assigned to.
   * @returns The IDs of the users customers.
   */
  public getUsersCustomerIds(): IUserGroup[] | undefined {
    if (!this.hasValidAccessToken()) {
      return undefined;
    }
    return this.currentUser?.groups.filter(x => x.userGroupType === EUserGroupTypes.Customer) ?? undefined;
  }

  /**
   * Checks if the current user is a Customer Admin.
   *
   * This method verifies if the current user has a valid access token and belongs to the 
   * 'DigitalApp' user group with the 'Admin' role.
   *
   * @returns {boolean} - Returns `true` if the user is a Customer Admin, otherwise `false`.
   */
  public isUserCustomerAdmin(): boolean {
    if (!this.hasValidAccessToken()) {
      return false;
    }
    var groups = this.currentUser?.groups.filter(x => x.userGroupType === EUserGroupTypes.DigitalApp && x.userGroupRole === EUserGroupRoles.Admin);
    if (groups !== undefined && groups?.length > 0) {
      return true;
    }
    return false;
  }

  /**
   * Gets the user ID of the currently authenticated user.
   * @returns The ID of the user.
   */
  public getCurrentUserId(): string | undefined {
    let claims = this.oAuthService.getIdentityClaims();
    if (!claims) {
      return undefined;
    }
    return claims.sub;
  }

  private convertStringToUserGroup(adGroupString: string): IUserGroup | undefined {
    if (adGroupString === '') {
      return undefined;
    }
    var splitted = adGroupString.split('-');
    if (environment.production) {
      if (splitted[1] === 'dev') {
        return undefined;
      }
    } else {
      if (splitted[1] === 'prod') {
        return undefined;
      }
    }
    if (splitted.length < 4) {
      return undefined;
    }
    var userGroup: IUserGroup | undefined;
    switch (splitted[2]) {
      case 'customer':
        var resultArray = splitted.slice(3, splitted.length);
        userGroup = new UserGroup(resultArray.join('-'), EUserGroupTypes.Customer, adGroupString, null, true, '');
        break;
      case 'bihlerDigital':
        userGroup = new UserGroup(adGroupString, EUserGroupTypes.BihlerDigital, '', null, true, '');
        break;
      case 'module':
        var module = splitted[3].split('_');

        if (module[1] === 'admin') {
          userGroup = new UserGroup(module[0] + ' Admin', EUserGroupTypes.Module, adGroupString, EUserGroupRoles.Admin, true, '');
        } else {
          userGroup = new UserGroup(module[0] + ' User', EUserGroupTypes.Module, adGroupString, EUserGroupRoles.User, true, '');
        }
        break;
      case 'machine':
        userGroup = new UserGroup(splitted[3], EUserGroupTypes.Machine, adGroupString, null, true, '');
        break;
      case 'digitalApp':
        var module = splitted[3].split('_');
        if (module[1] === 'admin') {
          userGroup = new UserGroup(module[0] + ' Admin', EUserGroupTypes.DigitalApp, adGroupString, EUserGroupRoles.Admin, true, '');
        } else {
          userGroup = new UserGroup(module[0] + ' User', EUserGroupTypes.DigitalApp, adGroupString, EUserGroupRoles.User, true, '');
        }
        break;
    }
    return userGroup;
  }

  /**
   * Unsubscribes from all open subscriptions.
   */
  ngOnDestroy(): void {
    if (this.subscriptions !== undefined) {
      this.subscriptions.unsubscribe();
    }
  }
}
